
import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Star, Shield, Clock, Users, ChevronDown, Menu, X, CheckCircle, ArrowRight, Palette, Home, Building, Sparkles } from 'lucide-react';
import './App.css';



const Homepage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeService, setActiveService] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveService((prev) => (prev + 1) % 6);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const services = [
    { title: "Exterior Painting", icon: <Home className="w-8 h-8" />, color: "from-red-500 to-orange-500" },
    { title: "Interior Design", icon: <Palette className="w-8 h-8" />, color: "from-purple-500 to-pink-500" },
    { title: "Commercial Projects", icon: <Building className="w-8 h-8" />, color: "from-blue-500 to-cyan-500" },
    { title: "Premium Finishes", icon: <Sparkles className="w-8 h-8" />, color: "from-yellow-500 to-orange-500" },
    { title: "Desert Climate Protection", icon: <Shield className="w-8 h-8" />, color: "from-green-500 to-emerald-500" },
    { title: "Custom Solutions", icon: <Users className="w-8 h-8" />, color: "from-indigo-500 to-purple-500" }
  ];

  return (
    <div className="container">
      {/* Navigation */}
      <nav className={`nav ${isScrolled ? 'nav-scrolled' : 'nav-transparent'}`}>
        <div className="max-width-lg">
          <div className="nav-container">
            <div className="nav-brand">
              <div className="nav-logo-container">
                <div className="nav-logo-main">
                  <span className="nav-logo-text">S</span>
                </div>
                <div className="nav-logo-glow"></div>
              </div>
              <div>
                <h1 className="nav-title-main">SlickRock</h1>
                <p className="nav-title-sub">PAINTING CO.</p>
              </div>
            </div>
            
            {/* Desktop Menu */}
            <div className="nav-menu-desktop">
              {['Services', 'Portfolio', 'About', 'Reviews'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="nav-link"
                >
                  {item}
                </button>
              ))}
              <button 
                onClick={() => scrollToSection('contact')}
                className="nav-button"
              >
                Get Quote
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="nav-menu-mobile"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="nav-menu-mobile-container">
            <div className="nav-menu-mobile-content">
              {['Services', 'Portfolio', 'About', 'Reviews'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="nav-link"
                  style={{ display: 'block', width: '100%', textAlign: 'left' }}
                >
                  {item}
                </button>
              ))}
              <button 
                onClick={() => scrollToSection('contact')}
                className="nav-button"
                style={{ width: '100%' }}
              >
                Get Quote
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-container">
        {/* Animated Background */}
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-orb-1"></div>
          <div className="hero-orb-2"></div>
          <div className="hero-spinner"></div>
        </div>

        <div className="hero-content">
          <div style={{ marginBottom: '2rem' }}>
            <div className="hero-badge">
              <div className="hero-badge-icon"></div>
              <span className="hero-badge-text">Southern Utah's Premier Painting Experts</span>
            </div>
          </div>

          <h1 className="hero-title-main">
            <span className="hero-title-block">Add Color To </span>
            <span className="hero-title-gradient">Your Life!</span>
          </h1>

          <p className="hero-subtitle">
            Where cutting-edge techniques meet Southern Utah's unique desert landscape. We don't just paint walls — we create experiences that last.
          </p>

          <div className="hero-buttons">
            <button 
              onClick={() => scrollToSection('contact')}
              className="hero-primary-button"
            >
              <div className="hero-primary-button-content">
                <span>Start Your Project</span>
                <ArrowRight className="hero-primary-button-icon" />
              </div>
              <div className="hero-primary-button-glow"></div>
            </button>
            <button 
              onClick={() => scrollToSection('portfolio')}
              className="hero-secondary-button"
            >
              View Portfolio
            </button>
          </div>

          {/* Animated Service Showcase */}
          <div className="service-showcase">
            {services.map((service, index) => (
              <div
                key={index}
                className={`service-card ${
                  activeService === index 
                    ? `service-card-active` 
                    : 'service-card-inactive'
                }`}
                style={activeService === index ? {
                  background: `linear-gradient(135deg, var(--gradient-from), var(--gradient-to))`,
                  '--gradient-from': service.color.includes('red') ? '#ef4444' : 
                                   service.color.includes('purple') ? '#a855f7' :
                                   service.color.includes('blue') ? '#3b82f6' :
                                   service.color.includes('yellow') ? '#eab308' :
                                   service.color.includes('green') ? '#10b981' : '#6366f1',
                  '--gradient-to': service.color.includes('orange') ? '#f97316' :
                                  service.color.includes('pink') ? '#ec4899' :
                                  service.color.includes('cyan') ? '#06b6d4' :
                                  service.color.includes('emerald') ? '#059669' :
                                  service.color.includes('purple') ? '#8b5cf6' : '#f97316'
                } : {}}
              >
                <div className="service-card-content">
                  <div className="service-card-icon">
                    {service.icon}
                  </div>
                  <p className="service-card-text">{service.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="scroll-indicator-container">
          <div className="scroll-indicator-content">
            <div className="scroll-indicator-line"></div>
            <ChevronDown className="scroll-indicator-icon" />
          </div>
        </div>
      </section>

      {/* Stats Section with Glassmorphism */}
      <section className="stats-section">
        <div className="stats-background"></div>
        <div className="max-width" style={{ position: 'relative', zIndex: 10 }}>
          <div className="stats-container">
            <div className="stats-grid">
              {[
                { number: "15+", label: "Years Mastering Desert Conditions", icon: <Clock className="stats-icon text-red-400" /> },
                { number: "850+", label: "Homes Transformed", icon: <Home className="stats-icon text-orange-400" /> },
                { number: "5★", label: "Average Rating", icon: <Star className="stats-icon text-yellow-400" style={{ fill: 'currentColor' }} /> },
                { number: "100%", label: "Satisfaction Guarantee", icon: <Shield className="stats-icon text-green-400" /> }
              ].map((stat, index) => (
                <div key={index} className="stats-card">
                  {stat.icon}
                  <div className="stats-number">
                    {stat.number}
                  </div>
                  <p className="stats-label">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section">
        <div className="max-width">
          <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
            <div className="services-badge">
              <span className="services-badge-text">Our Expertise</span>
            </div>
            <h2 className="services-title">Services That</h2>
            <h2 className="services-title-gradient">Redefine Excellence</h2>
            <p className="services-subtitle">Each service is crafted with precision, using cutting-edge techniques and premium materials designed for Southern Utah's extreme climate.</p>
          </div>

          <div className="services-grid">
            {[
              {
                title: "Exterior Mastery",
                description: "Military-grade UV protection meets artistic vision. Our exterior coatings don't just survive the desert — they thrive in it.",
                features: ["15-year fade resistance", "Thermal barrier technology", "Sandstorm-proof finishes"],
                gradient: "linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(249, 115, 22, 0.2))",
                border: "rgba(239, 68, 68, 0.3)",
                icon: <Home className="w-12 h-12 text-red-400" />
              },
              {
                title: "Interior Luxury",
                description: "Transform your sanctuary with premium finishes that reflect your lifestyle. Zero-VOC luxury that's safe for your family.",
                features: ["Designer color matching", "Texture specialists", "Air-purifying paints"],
                gradient: "linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(236, 72, 153, 0.2))",
                border: "rgba(168, 85, 247, 0.3)",
                icon: <Palette className="w-12 h-12 text-purple-400" />
              },
              {
                title: "Commercial Elite",
                description: "Fortune 500 standards for businesses that demand perfection. Minimal downtime, maximum impact.",
                features: ["24/7 project management", "Hospitality-grade finishes", "LEED-certified options"],
                gradient: "linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(6, 182, 212, 0.2))",
                border: "rgba(59, 130, 246, 0.3)",
                icon: <Building className="w-12 h-12 text-blue-400" />
              }
            ].map((service, index) => (
              <div 
                key={index} 
                className="services-card"
                style={{ 
                  background: service.gradient,
                  borderColor: service.border
                }}
              >
                <div className="services-card-icon">{service.icon}</div>
                <h3 className="services-card-title">{service.title}</h3>
                <p className="services-card-description">{service.description}</p>
                <div className="services-feature-list">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="services-feature">
                      <CheckCircle className="services-feature-icon" />
                      <span className="services-feature-text">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-large">
        <div className="about-background"></div>
        <div className="max-width" style={{ position: 'relative', zIndex: 10 }}>
          <div className="about-grid">
            <div>
              <div className="about-badge">
                <span className="about-badge-text">Why SlickRock</span>
              </div>
              <h2 className="about-title">
                Born from the
                <span className="about-title-gradient">Desert</span>
              </h2>
              <p className="about-description">
                We're not just painters — we're desert climate engineers. Our technique, Our Material Choice, Every brush stroke is informed by years of conquering Southern Utah's most challenging conditions.
              </p>
              
              <div className="about-item-list">
                {[
                  {
                    title: "Desert Climate Mastery",
                    description: "While others guess, we know. Our proprietary coating systems are battle-tested against 120°F heat and brutal UV exposure.",
                    gradient: "linear-gradient(135deg, #ef4444, #f97316)"
                  },
                  {
                    title: "Local Legacy",
                    description: "Born and raised in Southern Utah. We understand the land, the people, and what it takes to build something that lasts.",
                    gradient: "linear-gradient(135deg, #f97316, #eab308)"
                  },
                  {
                    title: "Innovation Obsessed",
                    description: "First in Utah to adopt thermal barrier coatings. First to offer 15-year fade warranties. Always pushing boundaries.",
                    gradient: "linear-gradient(135deg, #eab308, #ef4444)"
                  }
                ].map((item, index) => (
                  <div key={index} className="about-item">
                    <div 
                      className="about-item-icon"
                      style={{ background: item.gradient }}
                    >
                      <div className="about-item-icon-inner"></div>
                    </div>
                    <div className="about-item-content">
                      <h3 className="about-item-title">{item.title}</h3>
                      <p className="about-item-description">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div style={{ position: 'relative' }}>
              <div className="testimonial-card-container">
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <div className="testimonial-card-rating">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="testimonial-card-rating-icon" />
                    ))}
                  </div>
                  <div className="testimonial-card-score">4.9/5.0</div>
                  <p className="testimonial-card-score-label">From many verified reviews</p>
                </div>
                <blockquote className="testimonial-card-quote">
                  "SlickRock didn't just paint our home — they engineered a solution. Three years later, in this brutal desert sun, it still looks like day one. Absolutely incredible."
                </blockquote>
                <div className="testimonial-card-author">
                  <div className="testimonial-card-author-avatar">
                    M
                  </div>
                  <div>
                    <p className="testimonial-card-author-name">Erik Reisner</p>
                    <p className="testimonial-card-author-location">St. George Homeowner</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="section">
        <div className="max-width">
          <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
            <h2 className="reviews-title">Client Success Stories</h2>
            <p className="reviews-subtitle">Real results from real Southern Utah homeowners</p>
          </div>

          <div className="reviews-grid">
            {[
              {
                name: "Sarah & Mike Johnson",
                location: "St. George",
                project: "Full Exterior Renovation",
                text: "Three years in and our home still stops traffic. The UV protection is unreal — neighbors ask us weekly who did the work.",
                rating: 5,
                gradient: "linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(249, 115, 22, 0.1))",
                border: "rgba(239, 68, 68, 0.2)"
              },
              {
                name: "David Martinez",
                location: "Cedar City",
                project: "Commercial Office Complex",
                text: "Zero downtime, military precision, and a result that exceeded our highest expectations. True professionals.",
                rating: 5,
                gradient: "linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(6, 182, 212, 0.1))",
                border: "rgba(59, 130, 246, 0.2)"
              },
              {
                name: "Lisa Thompson",
                location: "Hurricane",
                project: "Luxury Interior Design",
                text: "They transformed our home into something from Architectural Digest. The attention to detail is phenomenal.",
                rating: 5,
                gradient: "linear-gradient(135deg, rgba(168, 85, 247, 0.1), rgba(236, 72, 153, 0.1))",
                border: "rgba(168, 85, 247, 0.2)"
              }
            ].map((review, index) => (
              <div 
                key={index} 
                className="reviews-card"
                style={{ 
                  background: review.gradient,
                  borderColor: review.border
                }}
              >
                <div className="reviews-rating">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="reviews-rating-icon" />
                  ))}
                </div>
                <blockquote className="reviews-quote">
                  "{review.text}"
                </blockquote>
                <div className="reviews-footer">
                  <p className="reviews-name">{review.name}</p>
                  <p className="reviews-location">{review.location}</p>
                  <p className="reviews-project">{review.project}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section 
      <section id="contact" className="section-large">
        <div className="contact-background"></div>
        <div className="max-width" style={{ position: 'relative', zIndex: 10 }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="contact-title">Ready to Begin?</h2>
            <p className="contact-subtitle">Let's create something extraordinary together</p>
          </div>

          <div className="contact-grid">
            <div>
              <h3 className="contact-left-title">Get Your Vision Started</h3>
              <p className="contact-left-description">
                Every masterpiece begins with a conversation. We'll visit your space, understand your vision, and engineer the perfect solution.
              </p>
              
              <div className="contact-list">
                {[
                  {
                    icon: <Phone style={{ width: '1.5rem', height: '1.5rem' }} />,
                    title: "Call or Text",
                    content: "(435) 414-0098",
                    gradient: "linear-gradient(135deg, #ef4444, #f97316)"
                  },
                  {
                    icon: <Mail style={{ width: '1.5rem', height: '1.5rem' }} />,
                    title: "Email",
                    content: "Sales@SlickRockPainting.com",
                    gradient: "linear-gradient(135deg, #f97316, #eab308)"
                  },
                  {
                    icon: <MapPin style={{ width: '1.5rem', height: '1.5rem' }} />,
                    title: "Service Area",
                    content: "All of Southern Utah",
                    gradient: "linear-gradient(135deg, #eab308, #ef4444)"
                  }
                ].map((contact, index) => (
                  <div key={index} className="contact-item">
                    <div 
                      className="contact-icon"
                      style={{ background: contact.gradient }}
                    >
                      {contact.icon}
                    </div>
                    <div>
                      <p style={{ color: '#9ca3af', fontSize: '0.875rem', fontWeight: 500 }}>{contact.title}</p>
                      <p style={{ fontSize: '1.5rem', fontWeight: 700, color: '#ffffff' }}>{contact.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="form-container">
              <h3 className="form-title">Start Your Project</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  <input 
                    type="text" 
                    placeholder="First Name" 
                    className="form-input"
                  />
                  <input 
                    type="text" 
                    placeholder="Last Name" 
                    className="form-input"
                  />
                </div>
                <input 
                  type="email" 
                  placeholder="Email Address" 
                  className="form-input-full"
                />
                <input 
                  type="tel" 
                  placeholder="Phone Number" 
                  className="form-input-full"
                />
                <select className="form-input-full">
                  <option value="" style={{ backgroundColor: '#1f2937' }}>Select Your Project Type</option>
                  <option value="exterior" style={{ backgroundColor: '#1f2937' }}>Exterior Painting</option>
                  <option value="interior" style={{ backgroundColor: '#1f2937' }}>Interior Painting</option>
                  <option value="both" style={{ backgroundColor: '#1f2937' }}>Complete Home Transformation</option>
                  <option value="commercial" style={{ backgroundColor: '#1f2937' }}>Commercial Project</option>
                  <option value="commercial" style={{ backgroundColor: '#1f2937' }}>Something Else</option>
                </select>
                <textarea 
                  placeholder="Tell us about your vision..." 
                  rows={4}
                  className="form-textarea"
                ></textarea>
                <button 
                  className="form-button"
                  onClick={() => alert('Thanks for your interest! We will be in touch soon.')}
                >
                  <span>Get Your Free Consultation</span>
                  <ArrowRight style={{ width: '1.25rem', height: '1.25rem' }} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>. */}


    </div>
  );
};

export default Homepage;
